import React, { Suspense, useEffect, useMemo } from "react";

import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import invariant from "tiny-invariant";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";
import { NotFoundPage } from "@/shared/components/common/NotFoundPage/NotFoundPage";

import { type LANGUAGE_ID } from "./shared/lang/lang.constants";

import {
  ConfigResolver,
  ResolverBootstrap,
  useBootstrap,
  useFarmSettings,
} from "./app.bootstrap";
import {
  InitializeGoogleTagManager,
  InitializeIntercom,
  InitializeLanguage,
  InitializeMixpanel,
  InitializeSentry,
} from "./app.initializers";
import {
  ActionsRoute,
  AdminRoute,
  CataloguesRoute,
  FarmIdRootRoute,
  FarmIndexRedirect,
  IrrigationRoute,
  LazyOAuthModule,
  MapRoute,
  NdwiReportRoute,
  NotificationsRoute,
  ParcelsRoute,
  PrecisionFarmingRoute,
  ReportsRoute,
  SensorsRoute,
  StoresRoute,
  TelematicsRoute,
  UserRoute,
} from "./app.routes";
import { LoginWrapper } from "./auth/login/LoginWrapper";
import { ResetWrapper } from "./auth/reset/ResetWrapper";
import { SignupWrapper } from "./auth/signup/SignupWrapper";
import { ChunkErrorFallback } from "./common/components/ChunkErrorFallback/ChunkErrorFallback";
import { FeatureFlagProvider } from "./common/providers/FeatureFlagProvider/FeatureFlagProvider";
import { buildNgProps } from "./core/navbar/build-ng-props";
import { NavbarWrapper } from "./core/navbar/containers/NavbarWrapper/NavbarWrapper";
import DynamicIntlProvider from "./shared/components/DynamicIntlProvider/DynamicIntlProvider";
import { BaseContainer } from "./shared/containers/BaseContainer/BaseContainer";
import CfApp from "./shared/containers/CfApp/CfApp";
import { FarmModulesProvider, ModuleRouteGuard } from "./shared/farm-modules";
import { FarmSettingsProvider } from "./shared/providers/FarmSettingsProvider";
import { queryClient } from "./shared/query-client";
import Localization from "./shared/services/Localization.service";
import { setMixpanelFarm } from "./shared/services/mixpanel";
import { getAppVariant } from "./shared/variants/getAppVariant";
import createCftheme from "./theme";

import { type ResolvedProps } from "./app.types";
import "./assets/index.css";

import "moment/dist/locale/cs";
import "moment/dist/locale/es";
import "moment/dist/locale/hu";
import "moment/dist/locale/lt";
import "moment/dist/locale/pl";
import "moment/dist/locale/ro";
import "moment/dist/locale/sk";

// @ts-expect-error it worked fine before, leaving it like this until we move to something else
momentDurationFormatSetup(moment);

export type { ResolvedProps };

const useResolvedFarm = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const { config, farms, user } = useBootstrap();

  const activeFarm = farms.find((f) => f.id === farmId) ?? farms[0];
  invariant(activeFarm, "No farm available");
  const farmSettings = useFarmSettings(activeFarm.id);

  useEffect(() => {
    if (activeFarm.id && activeFarm.name) {
      setMixpanelFarm(activeFarm.id, activeFarm.name);
    }
  }, [activeFarm.id, activeFarm.name]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- user is Record<string, never> (typed without authorities) before login
  const isAdmin = !!user?.authorities?.includes("ROLE_ADMIN");

  const resolved: ResolvedProps = useMemo(
    () => ({
      farmId: activeFarm.id,
      farmBbox: activeFarm.boundingBox ?? undefined,
      farms: farms.map((f) => ({
        id: f.id,
        name: f.name!,
        countryCode: activeFarm.countryCode,
        boundingBox: f.boundingBox ?? undefined,
      })),
      farm: {
        isAdmin,
        id: activeFarm.id,
        name: activeFarm.name!,
        countryCode: activeFarm.countryCode,
        boundingBox: activeFarm.boundingBox ?? undefined,
      },
      config,
    }),
    [activeFarm, config, farms, isAdmin],
  );

  return { resolved, farmSettings };
};

const FarmShell = () => {
  const { farmSettings, resolved } = useResolvedFarm();

  return (
    <FarmSettingsProvider settings={farmSettings}>
      <FeatureFlagProvider userId={resolved.farm.id}>
        <FarmModulesProvider farmId={resolved.farm.id}>
          <InitializeIntercom
            currentFarm={resolved.farm}
            farms={resolved.farms}
          >
            <NavbarWrapper ngProps={buildNgProps(resolved)}>
              <Outlet context={resolved} />
            </NavbarWrapper>
          </InitializeIntercom>
        </FarmModulesProvider>
      </FeatureFlagProvider>
    </FarmSettingsProvider>
  );
};

const NdwiReportShell = () => {
  const { farmSettings, resolved } = useResolvedFarm();

  return (
    <FarmSettingsProvider settings={farmSettings}>
      <FeatureFlagProvider userId={resolved.farm.id}>
        <DynamicIntlProvider locale={Localization.getLang() as LANGUAGE_ID}>
          <BaseContainer>
            <Outlet context={resolved} />
          </BaseContainer>
        </DynamicIntlProvider>
      </FeatureFlagProvider>
    </FarmSettingsProvider>
  );
};

const muiTheme = createTheme(createCftheme(getAppVariant()));

const AppRoutes = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={muiTheme}>
      <InitializeLanguage>
        <InitializeSentry>
          <InitializeGoogleTagManager>
            <InitializeMixpanel>
              <QueryClientProvider client={queryClient}>
                <ErrorBoundary
                  fallbackRender={({ error }) => (
                    <ChunkErrorFallback error={error} />
                  )}
                >
                  <BrowserRouter>
                    <Routes>
                      <Route element={<LoginWrapper />} path="/login" />

                      <Route
                        path="/reset"
                        element={
                          <ConfigResolver>
                            {(config) => (
                              <CfApp
                                config={config}
                                render={(p: Record<string, unknown>) => (
                                  <ResetWrapper {...p} />
                                )}
                              />
                            )}
                          </ConfigResolver>
                        }
                      />

                      <Route
                        path="/signup/*"
                        element={
                          <ConfigResolver>
                            {(config) => (
                              <CfApp
                                config={config}
                                render={() => <SignupWrapper />}
                              />
                            )}
                          </ConfigResolver>
                        }
                      />

                      <Route
                        path="/oauth/*"
                        element={
                          <Suspense fallback={<CfLoader size={20} />}>
                            <LazyOAuthModule />
                          </Suspense>
                        }
                      />

                      <Route element={<ResolverBootstrap />}>
                        <Route
                          element={<Navigate replace to="/farm" />}
                          path="/"
                        />
                        <Route element={<FarmIndexRedirect />} path="/farm" />

                        <Route
                          element={<NdwiReportShell />}
                          path="/farm/:farmId/reports/pre-harvest-maturity"
                        >
                          <Route element={<NdwiReportRoute />} index />
                        </Route>

                        <Route element={<FarmShell />} path="/farm/:farmId/*">
                          <Route element={<FarmIdRootRoute />} index />

                          <Route element={<ModuleRouteGuard />}>
                            <Route
                              element={<ActionsRoute />}
                              path="actions/*"
                            />
                            <Route
                              element={<ReportsRoute />}
                              path="reports/*"
                            />
                            <Route
                              element={<CataloguesRoute />}
                              path="catalogues/*"
                            />
                            <Route
                              element={<IrrigationRoute />}
                              path="irrigation/*"
                            />
                            <Route element={<StoresRoute />} path="stores/*" />
                            <Route
                              element={<TelematicsRoute />}
                              path="telematics/*"
                            />
                            <Route
                              element={<PrecisionFarmingRoute />}
                              path="precision-farming/*"
                            />
                          </Route>

                          <Route element={<UserRoute />} path="user/*" />
                          <Route element={<MapRoute />} path="map/*" />
                          <Route element={<AdminRoute />} path="admin/*" />
                          <Route
                            element={<NotificationsRoute />}
                            path="notifications/*"
                          />
                          <Route element={<SensorsRoute />} path="sensors/*" />
                          <Route element={<ParcelsRoute />} path="parcels/*" />

                          <Route element={<NotFoundPage />} path="*" />
                        </Route>

                        <Route element={<NotFoundPage />} path="*" />
                      </Route>

                      <Route
                        element={<Navigate replace to="/farm" />}
                        path="*"
                      />
                    </Routes>
                  </BrowserRouter>
                </ErrorBoundary>
              </QueryClientProvider>
            </InitializeMixpanel>
          </InitializeGoogleTagManager>
        </InitializeSentry>
      </InitializeLanguage>
    </ThemeProvider>
  </StyledEngineProvider>
);

const root = createRoot(document.querySelector("#app")!);
root.render(
  <React.StrictMode>
    <AppRoutes />
  </React.StrictMode>,
);
